import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiMapPin, FiCheck } from 'react-icons/fi';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import BaseUrl from '../../../Url/BaseUrl';

const EditRoute = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    routeVillagesPathNo: '',
    routePathNo: '',
    cityVillageCode: '',
    status: true
  });
  
  // Master data
  const [villages, setVillages] = useState([]);
  const [routePaths, setRoutePaths] = useState([]);
  const [loadingMasterData, setLoadingMasterData] = useState(false);
  
  // API base URL
  const API_BASE_URL = BaseUrl.baseurl

  // Fetch route data and master data
  useEffect(() => {
    fetchRouteData();
    fetchMasterData();
  }, [id]);

  // Fetch specific route data
  const fetchRouteData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}RoutePathVillages/${id}`);
      if (response.data) {
        setFormData({
          routeVillagesPathNo: response.data.routeVillagesPathNo || '',
          routePathNo: response.data.routePathNo || '',
          cityVillageCode: response.data.cityVillageCode || '',
          status: response.data.status !== undefined ? response.data.status : true
        });
      }
    } catch (err) {
      setError(t('failedToLoadRouteData'));
      console.error('Error fetching route:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch master data (villages and route paths)
  const fetchMasterData = async () => {
    setLoadingMasterData(true);
    try {
      // Fetch villages
      const villagesResponse = await axios.get(`${API_BASE_URL}CityVillages`);
      const sortedVillages = villagesResponse.data.sort((a, b) => 
        a.cityVillageName.localeCompare(b.cityVillageName)
      );
      setVillages(sortedVillages);

      // Fetch route paths
      const routesResponse = await axios.get(`${API_BASE_URL}RoutePaths`);
      setRoutePaths(routesResponse.data);
    } catch (err) {
      setError(t('failedToLoadMasterData'));
      console.error('Error fetching master data:', err);
    } finally {
      setLoadingMasterData(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.routeVillagesPathNo.trim()) {
      alert(t('routeVillagesPathNoRequired'));
      return;
    }
    
    if (!formData.routePathNo.trim()) {
      alert(t('routePathNoRequired'));
      return;
    }
    
    if (!formData.cityVillageCode.trim()) {
      alert(t('cityVillageCodeRequired'));
      return;
    }

    setSaving(true);
    setError(null);
    
    try {
      // Prepare the payload according to API schema
      const payload = {
        routeVillagesPathNo: formData.routeVillagesPathNo,
        routePathNo: formData.routePathNo,
        routePath: null, // As per API example
        cityVillageCode: formData.cityVillageCode,
        cityVillage: null, // As per API example
        status: formData.status
      };

      console.log('Updating route with payload:', payload);

      await axios.put(`${API_BASE_URL}RoutePathVillages/${id}`, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      setSuccess(true);
      
      // Redirect after 2 seconds
      setTimeout(() => {
        navigate('/route-finder/routes/manage');
      }, 2000);
      
    } catch (err) {
      setError(t('failedToUpdateRoute'));
      console.error('Error updating route:', err.response?.data || err.message);
    } finally {
      setSaving(false);
    }
  };

  // Get village name by code
  const getVillageName = (code) => {
    const village = villages.find(v => v.cityVillageCode === code);
    return village ? village.cityVillageName : t('unknownVillage');
  };

  // Get route path name by code
  const getRoutePathName = (code) => {
    const routePath = routePaths.find(r => r.routePathNo === code);
    return routePath ? routePath.routePathName : t('unknownRoute');
  };

  if (loading) {
    return (
      <div className="management-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t('loadingRouteData')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="management-container">
      {/* Header */}
      <div className="management-header">
        <div className="header-actions">
          <button 
            className="btn-secondary"
            onClick={() => navigate('/route-finder/routes/manage')}
          >
            <FiArrowLeft /> {t('backToRoutes')}
          </button>
        </div>
        <h2>{t('editBusRoute')}</h2>
        <p>{t('updateRouteDetails', { id })}</p>
      </div>

      {/* Success Message */}
      {success && (
        <div className="success-message">
          <FiCheck className="success-icon" />
          <div>
            <h3>{t('routeUpdatedSuccessfully')}</h3>
            <p>{t('redirectingToRoutes')}</p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="error-state">
          <p className="error-message">{error}</p>
        </div>
      )}

      {/* Edit Form */}
      {!success && (
        <div className="edit-form-container">
          <form onSubmit={handleSubmit} className="edit-form">
            {/* Route Villages Path No */}
            <div className="form-group">
              <label htmlFor="routeVillagesPathNo">
                {t('routeVillagesPathNo')} *
              </label>
              <input
                type="text"
                id="routeVillagesPathNo"
                name="routeVillagesPathNo"
                value={formData.routeVillagesPathNo}
                onChange={handleInputChange}
                className="form-input"
                placeholder={t('enterRouteVillagesPathNumber')}
                required
              />
            </div>

            {/* Route Path Selection */}
            <div className="form-group">
              <label htmlFor="routePathNo">
                {t('routePath')} *
                {formData.routePathNo && (
                  <span className="current-selection">
                    {t('current')}: {getRoutePathName(formData.routePathNo)}
                  </span>
                )}
              </label>
              <select
                id="routePathNo"
                name="routePathNo"
                value={formData.routePathNo}
                onChange={handleInputChange}
                className="form-input"
                required
                disabled={loadingMasterData}
              >
                <option value="">{t('selectRoutePath')}</option>
                {routePaths.map(routePath => (
                  <option key={routePath.routePathNo} value={routePath.routePathNo}>
                    {routePath.routePathNo} - {routePath.routePathName}
                  </option>
                ))}
              </select>
              {loadingMasterData && <small>{t('loadingRoutePaths')}</small>}
            </div>

            {/* City Village Selection */}
            <div className="form-group">
              <label htmlFor="cityVillageCode">
                {t('cityVillage')} *
                {formData.cityVillageCode && (
                  <span className="current-selection">
                    {t('current')}: {getVillageName(formData.cityVillageCode)}
                  </span>
                )}
              </label>
              <select
                id="cityVillageCode"
                name="cityVillageCode"
                value={formData.cityVillageCode}
                onChange={handleInputChange}
                className="form-input"
                required
                disabled={loadingMasterData}
              >
                <option value="">{t('selectCityVillage')}</option>
                {villages.map(village => (
                  <option key={village.cityVillageCode} value={village.cityVillageCode}>
                    {village.cityVillageCode} - {village.cityVillageName} ({village.districtCode})
                  </option>
                ))}
              </select>
              {loadingMasterData && <small>{t('loadingVillages')}</small>}
            </div>

            {/* Status */}
            <div className="form-group status-group">
              <label className="status-label">
                <input
                  type="checkbox"
                  name="status"
                  checked={formData.status}
                  onChange={handleInputChange}
                  className="status-checkbox"
                />
                <span className={`status-indicator ${formData.status ? 'active' : 'inactive'}`}>
                  {formData.status ? t('active') : t('inactive')}
                </span>
              </label>
              <small className="status-hint">
                {t('activeRoutesHint')}
              </small>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => navigate('/route-finder/routes/manage')}
                disabled={saving}
              >
                {t('cancel')}
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={saving || loadingMasterData}
              >
                {saving ? (
                  <>
                    <div className="spinner-small"></div>
                    {t('saving')}
                  </>
                ) : (
                  <>
                    <FiSave /> {t('saveChanges')}
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Current Information Card */}
          <div className="info-card">
            <h3><FiMapPin /> {t('currentRouteInformation')}</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">{t('routeVillagesPathNo')}:</span>
                <span className="info-value">{formData.routeVillagesPathNo || t('notSet')}</span>
              </div>
              <div className="info-item">
                <span className="info-label">{t('routePath')}:</span>
                <span className="info-value">
                  {formData.routePathNo ? `${formData.routePathNo} - ${getRoutePathName(formData.routePathNo)}` : t('notSet')}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">{t('cityVillage')}:</span>
                <span className="info-value">
                  {formData.cityVillageCode ? `${formData.cityVillageCode} - ${getVillageName(formData.cityVillageCode)}` : t('notSet')}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">{t('currentStatus')}:</span>
                <span className={`info-value status-${formData.status ? 'active' : 'inactive'}`}>
                  {formData.status ? t('active') : t('inactive')}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading overlay for master data */}
      {loadingMasterData && !loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>{t('loadingMasterData')}</p>
        </div>
      )}
    </div>
  );
};

export default EditRoute;